import { createApp } from 'vue'
// Components
import { registerGlobalComponents } from '@/components'

// Plugins
import { axiosPlugin, vuetify } from '@/plugins'
import { Auth0Plugin } from '@/plugins/auth0'

// Sync router with store (inlined vuex-router-sync replacement)
import { sync } from '@/utils/router-sync'

import App from '@/App.vue'
import i18n from '@/i18n'
import router from '@/router'
import { attachSessionIdToJigApiRequest, initSession } from '@/services/session-service'
import store from '@/store'
import { isLocalDashboardHost } from '@/utils/dashboard-host'
import { createGtm } from '@gtm-support/vue-gtm'
import { VueHeadMixin } from '@unhead/vue'
import { createHead } from '@unhead/vue/client'
import Axios from 'axios'

declare var consoleWarn: any

// Attach the per-page-load X-Jig-Session-Id to Jig API requests made through
// shared Axios. The bootstrap POST and isolated public-asset clients are excluded.
Axios.interceptors.request.use(attachSessionIdToJigApiRequest)

// Fire the session bootstrap immediately so it runs in parallel with the
// Auth0 token exchange. The interceptor awaits the resulting promise per
// request, so the first authenticated call is unblocked the moment this resolves.
initSession()

const isInviteSetupInitialRoute = window.location.pathname === '/pre-login' && /(?:\?|&)invited=1(?:&|$)/.test(window.location.search)
const isPublicPreLoginInitialRoute = (window.location.pathname === '/pre-login' || window.location.pathname === '/') && !isInviteSetupInitialRoute

const app = createApp(App)

app.use(router)
app.use(store)
app.use(i18n)
app.use(vuetify)

app.use(
  createGtm({
    // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryPararms: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}]
    id: 'GTM-P8WSRF2',
    // queryParams: {
    //   // Add url query string when load gtm.js with GTM ID (optional)
    //   gtm_auth: 'AB7cDEf3GHIjkl-MnOP8qr',
    //   gtm_preview: 'env-4',
    //   gtm_cookies_win: 'x',
    // },
    defer: true, // defaults to false. Script can be set to `defer` to increase page-load-time at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible)
    enabled: !isPublicPreLoginInitialRoute, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: process.env.VUE_APP_DEV_TOOLS === 'true', // Whether or not display console logs debugs (optional)
    loadScript: !isPublicPreLoginInitialRoute, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    // callback is ignored to avoid sending #access_token=123.456.789 in the path as that is PII.
    // The route is named 'Callback' and @gtm-support/vue-gtm@3 matches ignoredViews case-sensitively,
    // so use a predicate to keep the case-insensitive match of the old pinned vue-gtm@3.4.1-vue2.
    ignoredViews: (to) => typeof to.name === 'string' && to.name.toLowerCase() === 'callback',
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
)

// A localhost origin (local dev, or a CI-driven dev server pointed at a remote
// backend) must round-trip the Auth0 login back to itself; deployed builds use
// their canonical dashboard URL. Keyed off the actual runtime origin, not
// VUE_APP_DASHBOARD_ENV, which holds the target backend's env name (e.g.
// 'test') even when the app is served from localhost.
const redirectUriDomain =
  isLocalDashboardHost(window.location.hostname)
    ? window.location.origin
    : `https://dashboard.${(process.env.VUE_APP_WEB_DOMAIN as string).replace(/^http(s)?:\/\//, '').replace(/\/$/g, '')}`
app.use(Auth0Plugin, {
  audience: process.env.VUE_APP_AUDIENCE,
  clientId: process.env.VUE_APP_CLIENT_ID,
  domain: process.env.VUE_APP_DOMAIN,
  redirectUri: `${redirectUriDomain}/callback`,
  scope: 'openid profile email',
})

// @unhead/vue replaces vue-meta; the mixin enables the options-API `head` option.
app.use(createHead())
app.mixin(VueHeadMixin)

// $http (axios) global property
app.use(axiosPlugin)

// Global auto-registration of all components (require.context replacement)
registerGlobalComponents(app)

app.config.globalProperties.$analytics = (window as any).analytics

/* eslint-disable no-console */
// Extend global properties to add $callRefMethod
// It checkes if a ref and relevant method exists. Return true if ref.method has successfully processed, otherwise return false.
// prettier-ignore
app.config.globalProperties.$callRefMethod = function <T extends Record<string, any>> (this: any, refName: string, methodName: keyof T, ...args: any[]): boolean {
  const ref = this.$refs[refName]
  if (Array.isArray(ref)) {
    if (ref.length === 0) {
      consoleWarn(`Ref '${refName}' is an empty array. (methodName: ${String(methodName)})`)
      return false
    }
    if (typeof ref[0][methodName] !== 'function') {
      consoleWarn(`Method '${String(methodName)}' not found on ref '${refName}'`)
      return false
    }
    ref[0][methodName](...args)
    return true
  } else if (!ref) {
    consoleWarn(`Ref '${refName}' not found. (methodName: ${String(methodName)})`)
    return false
  } else if (typeof ref[methodName] !== 'function') {
    consoleWarn(`Method '${String(methodName)}' not found on ref '${refName}'`)
    return false
  } else {
    ref[methodName](...args)
    return true
  }
}

// Sync store with router
sync(store, router)

app.mount('#app')
